// pages/LeadDetail.jsx - WITH QUARANTINE INTEGRATION
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Copy,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building2,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Star,
  MoreVertical,
  FileText,
  Paperclip,
  MessageSquare,
  Activity,
  DollarSign,
  TrendingUp,
  Users,
  ExternalLink,
  Plus,
  Send,
  PhoneCall,
  Video,
  Target,
  Briefcase,
  Globe,
  Linkedin,
  Twitter,
  Facebook,
  AlertCircle,
  Save,
  X as CloseIcon,
  Loader,
  ShieldCheck,
  ListOrdered,
} from "lucide-react";
import apiService from "../../services/api";
import EditLeadModal from "../../components/EditLeadModal";
import BookAppointmentModal from "../../components/BookAppointmentModal";
import CreateTaskModal from "../../components/CreateTaskModal";
import ConvertLeadModal from "../../components/ConvertLeadModal";
import SendEmailModal from "../../components/SendEmailModal";
import LogCallModal from "../../components/LogCallModal";
import SendLeadEmailModal from "../../components/SendLeadEmailModal";
import LeadEmailThread from "../../components/LeadEmailThread";
import QuarantineLeadModal from "../../components/QuarantineLeadModal";
import PhoneLink from "../../components/PhoneLink";
import CallNotesTimeline from "../../components/CallNotesTimeline";
import { useTheme } from "../../contexts/ThemeContext";

// Extracted EditableField component - defined OUTSIDE the parent component
const EditableField = ({
  label,
  field,
  value,
  type = "text",
  options = null,
  readOnly = false,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const { isDarkMode } = useTheme();

  // Format value for editing
  const getEditValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          const date = new Date(val);
          return date.toISOString().split("T")[0];
        } catch {
          return val || "";
        }
      }
      return val || "";
    },
    [type],
  );

  // Format value for display
  // In the EditableField component, update the getDisplayValue function (around line 60)
  const getDisplayValue = useCallback(
    (val) => {
      if (type === "date" && val) {
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      }
      // If we have options and the value matches an option's value, show the label
      if (options && val) {
        const option = options.find((opt) => opt.value === val);
        if (option) return option.label;
      }
      return val || "—";
    },
    [type, options],
  );

  // Start editing
  const startEditing = useCallback(() => {
    setLocalValue(getEditValue(value));
    setIsEditing(true);
  }, [value, getEditValue]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setLocalValue("");
  }, []);

  // Save changes
  const saveChanges = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onSave(field, localValue);
      setIsEditing(false);
      setLocalValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  }, [field, localValue, onSave, isSaving]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        cancelEditing();
      } else if (e.key === "Enter" && type !== "textarea") {
        e.preventDefault();
        saveChanges();
      } else if (e.key === "Enter" && e.ctrlKey && type === "textarea") {
        e.preventDefault();
        saveChanges();
      }
    },
    [cancelEditing, saveChanges, type],
  );

  // Focus input when editing starts
  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Move cursor to end for text inputs (select elements don't support setSelectionRange)
          if (
            type === "text" ||
            type === "textarea" ||
            type === "email" ||
            type === "tel" ||
            type === "url"
          ) {
            const len = inputRef.current.value?.length || 0;
            // Safety check for setSelectionRange method
            if (inputRef.current.setSelectionRange) {
              inputRef.current.setSelectionRange(len, len);
            }
          }
        }
      });
    }
  }, [isEditing, type]);

  // Handle click outside to cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        cancelEditing();
      }
    };

    // Delay adding listener to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, cancelEditing]);

  if (readOnly) {
    return (
      <div>
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <p className={isDarkMode ? "text-white" : "text-gray-900"}>
          {getDisplayValue(value)}
        </p>
      </div>
    );
  }

  return (
    <div className="group" ref={containerRef}>
      {label && (
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            {options ? (
              <select
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                ref={inputRef}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                rows={3}
              />
            ) : type === "date" ? (
              <input
                ref={inputRef}
                type="date"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />
            ) : (
              <input
                ref={inputRef}
                type={type}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />
            )}
            <button
              onClick={saveChanges}
              disabled={isSaving}
              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              type="button"
            >
              {isSaving ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
            </button>
            <button
              onClick={cancelEditing}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              type="button"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        ) : (
          <>
            <p
              className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {getDisplayValue(value)}
            </p>
            <button
              onClick={startEditing}
              className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              type="button"
            >
              <Edit
                size={14}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showLogCall, setShowLogCall] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [emailConnectionStatus, setEmailConnectionStatus] = useState(null);
  const [checkingEmailConnection, setCheckingEmailConnection] = useState(false);
  const [users, setUsers] = useState([]);

  // NEW: Quarantine modal state
  const [showQuarantineModal, setShowQuarantineModal] = useState(false);
  const [pendingQuarantineStatus, setPendingQuarantineStatus] = useState(null);

  // Related data
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [engagementSteps, setEngagementSteps] = useState([]);
  const [relatedRecords, setRelatedRecords] = useState({
    appointments: [],
    tasks: [],
    opportunities: [],
    account: null,
    contact: null,
  });

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Lead Status definitions
  const leadStatuses = [
    { key: "unqualified", label: "Unqualified", color: "gray" },
    { key: "lead_pot", label: "Lead Pot", color: "blue" },
    { key: "new", label: "New", color: "cyan" },
    { key: "nurture", label: "Nurture", color: "yellow" },
    { key: "converted", label: "Converted", color: "green" },
    { key: "dead", label: "Dead", color: "red" },
  ];

  useEffect(() => {
    fetchLead();
    fetchNotes();
    fetchAttachments();
    fetchRelatedRecords();
    markAsViewed();
    checkEmailConnection();
  }, [id]);
  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  // Add this useEffect to fetch users when component mounts (add it with your other useEffects, around line 160)
  useEffect(() => {
    fetchUsers();
  }, []);
  const fetchLead = async (bustCache = false) => {
    try {
      setLoading(true);
      // Add cache-busting parameter AND force new request
      const timestamp = Date.now();
      const url = `${id}?_t=${timestamp}&bustCache=true`;
      const response = await apiService.getLead(url);
      setLead(response.data);
      // Extract engagement steps if included in the lead response
      if (response.data?.engagementSteps) {
        setEngagementSteps(response.data.engagementSteps);
      }
    } catch (error) {
      console.error("Error fetching lead:", error);
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getLeadNotes(id);
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getLeadAttachments(id);
      setAttachments(response.data);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const fetchRelatedRecords = async () => {
    try {
      const response = await apiService.getRelatedRecords(id);
      setRelatedRecords(response.data);
    } catch (error) {
      console.error("Error fetching related records:", error);
    }
  };

  const checkEmailConnection = async () => {
    try {
      setCheckingEmailConnection(true);
      const response = await apiService.getEmailConnectionStatus();
      setEmailConnectionStatus(response.data);
    } catch (error) {
      console.error("Error checking email connection:", error);
      setEmailConnectionStatus({ connected: false });
    } finally {
      setCheckingEmailConnection(false);
    }
  };

  useEffect(() => {
    const fetchConvertedRecords = async () => {
      if (!lead) return;

      try {
        if (lead.convertedAccountId) {
          const accountRes = await apiService.getAccount(
            lead.convertedAccountId,
          );
          setRelatedRecords((prev) => ({ ...prev, account: accountRes.data }));
        }
        if (lead.convertedContactId) {
          const contactRes = await apiService.getContact(
            lead.convertedContactId,
          );
          setRelatedRecords((prev) => ({ ...prev, contact: contactRes.data }));
        }
      } catch (error) {
        console.error("Error fetching converted records:", error);
      }
    };

    fetchConvertedRecords();
  }, [lead]);

  const markAsViewed = async () => {
    try {
      await apiService.markAsViewed(id);
    } catch (error) {
      console.error("Error marking as viewed:", error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await apiService.deleteLead(id);
        navigate("/leads");
      } catch (error) {
        console.error("Error deleting lead:", error);
        alert("Failed to delete lead");
      }
    }
  };
  const handleMoveToCleansed = async () => {
    if (
      !window.confirm(
        "Move this lead to the Cleansed Pot? It will be removed from Leads.",
      )
    )
      return;
    try {
      await apiService.moveToCleansed(lead.id);
      navigate("/leads"); // or wherever your leads list lives
    } catch (err) {
      console.error("Failed to move lead:", err);
    }
  };

  const handleClone = async () => {
    try {
      const response = await apiService.cloneLead(id);
      navigate(`/leads/${response.data.id}`);
    } catch (error) {
      console.error("Error cloning lead:", error);
      alert("Failed to clone lead");
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await apiService.createLeadNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadLeadAttachment(id, formData);
      fetchAttachments();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  // UPDATED: Handle lead status click with quarantine logic
  const handleLeadStatusClick = (newStatus) => {
    if (lead.isConverted) return;

    // If changing to "lead_pot", show quarantine modal instead of regular confirmation
    if (newStatus === "lead_pot") {
      setPendingQuarantineStatus(newStatus);
      setShowQuarantineModal(true);
    } else {
      setPendingStatus(newStatus);
      setShowStatusConfirmation(true);
    }
  };

  // NEW: Handle quarantine confirmation
  const handleQuarantineConfirm = async (quarantineData) => {
    try {
      // First update the lead status to lead_pot
      await apiService.updateLead(id, { leadStatus: pendingQuarantineStatus });

      // Then quarantine the lead with the specified data
      await apiService.quarantineLead(id, quarantineData);

      // Refresh lead data
      await fetchLead(true);

      // Close modal and reset state
      setShowQuarantineModal(false);
      setPendingQuarantineStatus(null);

      // Show success message
      alert("Lead has been quarantined successfully");
    } catch (error) {
      console.error("Error quarantining lead:", error);
      alert("Failed to quarantine lead: " + (error.message || "Unknown error"));
    }
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;

    try {
      // If converting to "converted" status, use the convertToOpportunity endpoint
      if (pendingStatus === "converted" && !lead.isConverted) {
        // Use the conversion API to create opportunity automatically
        const conversionResponse = await apiService.convertLeadToOpportunity(
          id,
          {
            doNotCreateOpportunity: false,
            createOpportunity: true,
            opportunityName: `${lead.companyName || lead.fullName} - Opportunity`,
            opportunityDescription:
              lead.description ||
              `Opportunity created from lead: ${lead.fullName}`,
            opportunityAmount: lead.annualRevenue || null,
            opportunityCurrency: "GBP",
            opportunityStage: "qualification",
            opportunityStatus: "open",
            opportunityPriority:
              lead.rating === "Hot"
                ? "high"
                : lead.rating === "Warm"
                  ? "medium"
                  : "low",
          },
        );

        if (conversionResponse.success) {
          setShowStatusConfirmation(false);
          setPendingStatus(null);
          await fetchLead();
          await fetchRelatedRecords();

          // Show success message with link to opportunity
          const opportunityId = conversionResponse.data?.opportunity?.id;
          if (opportunityId) {
            const viewOpportunity = window.confirm(
              `Lead converted successfully! An opportunity has been created.\n\nWould you like to view the opportunity now?`,
            );
            if (viewOpportunity) {
              navigate(`/opportunities/${opportunityId}`);
            }
          }
        } else {
          throw new Error(conversionResponse.message || "Conversion failed");
        }
      } else {
        // For other status changes, just update the lead status
        await apiService.updateLead(id, { leadStatus: pendingStatus });
        setShowStatusConfirmation(false);
        setPendingStatus(null);
        await fetchLead();
      }
    } catch (error) {
      console.error("Error updating lead status:", error);
      alert(
        "Failed to update lead status: " + (error.message || "Unknown error"),
      );
      setShowStatusConfirmation(false);
      setPendingStatus(null);
    }
  };

  const cancelStatusChange = () => {
    setShowStatusConfirmation(false);
    setPendingStatus(null);
  };

  // NEW: Cancel quarantine
  const cancelQuarantine = () => {
    setShowQuarantineModal(false);
    setPendingQuarantineStatus(null);
  };

  // Stable save handler using useCallback
  const handleFieldSave = useCallback(
    async (field, value) => {
      try {
        // Update the lead
        await apiService.updateLead(id, { [field]: value });

        // ⚠️ FORCE cache bust after update
        await fetchLead(true);

        // Optional: Show success toast
        console.log("Field updated successfully");
      } catch (error) {
        console.error("Failed to update field:", error);
        throw error; // Re-throw so EditableField can show error
      }
    },
    [id],
  );

  const handleEmailClick = () => {
    if (!emailConnectionStatus?.connected) {
      if (
        window.confirm(
          "You need to connect your email first. Would you like to go to Email Settings?",
        )
      ) {
        navigate("/settings/emails");
      }
      return;
    }
    setShowSendEmail(true);
  };

  const getLeadStatusColor = (status) => {
    const colors = {
      unqualified: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      lead_pot: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      new: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
      nurture: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      converted: "text-green-400 bg-green-400/10 border-green-400/20",
      dead: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const formatLeadStatus = (status) => {
    if (!status) return "None";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getCurrentLeadStatusIndex = () => {
    if (!lead?.leadStatus) return -1;
    return leadStatuses.findIndex((s) => s.key === lead.leadStatus);
  };

  const getStatusChangeMessage = (status) => {
    const messages = {
      unqualified:
        "Mark this lead as unqualified? This indicates the lead doesn't meet your criteria.",
      lead_pot:
        "Move this lead to the Lead Pot? This is a holding area for potential leads.",
      new: "Mark this lead as New? This indicates a fresh lead that needs attention.",
      nurture:
        "Move this lead to Nurture? This indicates the lead needs ongoing engagement.",
      converted:
        "Convert this lead? This will open the conversion wizard to create an Account, Contact, and Opportunity.",
      dead: "Mark this lead as Dead? This indicates the lead is no longer viable.",
    };
    return messages[status] || "Change the lead status?";
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!lead) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Lead not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      {/* Status Confirmation Modal */}
      {showStatusConfirmation && pendingStatus && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pendingStatus === "dead" || pendingStatus === "unqualified"
                    ? "bg-red-500/10"
                    : pendingStatus === "converted"
                      ? "bg-green-500/10"
                      : "bg-blue-500/10"
                }`}
              >
                {pendingStatus === "converted" ? (
                  <CheckCircle className="text-green-400" size={24} />
                ) : pendingStatus === "dead" ||
                  pendingStatus === "unqualified" ? (
                  <XCircle className="text-red-400" size={24} />
                ) : (
                  <AlertCircle className="text-blue-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Confirm Status Change
                </h3>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {getStatusChangeMessage(pendingStatus)}
                </p>
                <div
                  className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Current Status:
                    </span>
                    <span
                      className={`px-2 py-1 rounded ${getLeadStatusColor(lead.leadStatus)}`}
                    >
                      {formatLeadStatus(lead.leadStatus)}
                    </span>
                  </div>
                  <div className="flex items-center justify-center my-2">
                    <ArrowLeft
                      className={`${isDarkMode ? "text-gray-600" : "text-gray-400"} rotate-180`}
                      size={20}
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      New Status:
                    </span>
                    <span
                      className={`px-2 py-1 rounded ${getLeadStatusColor(pendingStatus)}`}
                    >
                      {formatLeadStatus(pendingStatus)}
                    </span>
                  </div>
                </div>
                {pendingStatus === "converted" && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 text-xs">
                      <strong>Note:</strong> After confirming, the conversion
                      wizard will open.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={cancelStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  pendingStatus === "converted"
                    ? "bg-green-600 hover:bg-green-700"
                    : pendingStatus === "dead" ||
                        pendingStatus === "unqualified"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Quarantine Lead Modal */}
      <QuarantineLeadModal
        isOpen={showQuarantineModal}
        onClose={cancelQuarantine}
        onConfirm={handleQuarantineConfirm}
        leadName={lead?.fullName || lead?.email || "This lead"}
      />

      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Quick Actions */}
              <button
                onClick={handleEmailClick}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                title={
                  !emailConnectionStatus?.connected
                    ? "Connect email first"
                    : "Send email"
                }
              >
                <Mail size={16} />
                <span className="hidden sm:inline">Email</span>
                {!emailConnectionStatus?.connected && (
                  <AlertCircle size={14} className="text-yellow-400" />
                )}
              </button>

              <button
                onClick={() => setShowLogCall(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <PhoneCall size={16} />
                <span className="hidden sm:inline">Log Call</span>
              </button>
              <button
                onClick={handleMoveToCleansed}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Move to Cleansed Pot</span>
              </button>

              <button
                onClick={() => setShowBookAppointment(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <Calendar size={16} />
                <span className="hidden sm:inline">Schedule</span>
              </button>

              <button
                onClick={() => setShowCreateTask(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <CheckCircle size={16} />
                <span className="hidden sm:inline">Task</span>
              </button>

              {/* Edit Button */}
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Edit size={16} />
                <span className="hidden sm:inline">Edit</span>
              </button>

              {/* Actions Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-red-500/10 flex items-center gap-2 text-red-400"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lead Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1
                  className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {lead.salutation} {lead.fullName || lead.email}
                </h1>

                {/* Lead Status Badge */}
                {lead.leadStatus && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${getLeadStatusColor(
                      lead.leadStatus,
                    )}`}
                  >
                    {formatLeadStatus(lead.leadStatus)}
                  </span>
                )}
              </div>
              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {lead.companyName && (
                  <div className="flex items-center gap-2">
                    <Building2 size={16} />
                    <span>{lead.companyName}</span>
                  </div>
                )}
                {lead.jobTitle && (
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} />
                    <span>{lead.jobTitle}</span>
                  </div>
                )}
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Mail size={16} />
                    <span>{lead.email}</span>
                  </a>
                )}
                {lead.phone && (
                  <PhoneLink
                    phoneNumber={lead.phone}
                    entityType="lead"
                    entityId={lead.id}
                    entityName={lead.fullName || lead.email}
                  />
                )}
              </div>
              {/* Tags */}
              {lead.tags && lead.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {lead.tags.map((tag, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 rounded text-xs ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Assigned User */}
            {lead.assignedUser && (
              <div
                className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                >
                  {lead.assignedUser.profileImageUrl ? (
                    <img
                      src={lead.assignedUser.profileImageUrl}
                      alt={lead.assignedUser.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                    >
                      {lead.assignedUser.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </div>
                  <div
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {lead.assignedUser.name}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Lead Status Path */}
          <div
            className={`mt-4 rounded-lg p-3 sm:p-4 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-100"}`}
          >
            <h3
              className={`text-sm font-medium mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Lead Status
            </h3>
            <div className="flex items-center justify-between overflow-x-auto pb-1 gap-1">
              {leadStatuses.map((status, index) => {
                const currentIndex = getCurrentLeadStatusIndex();
                const isActive = index === currentIndex;
                const isCompleted = index < currentIndex;
                const isCurrent = index <= currentIndex;

                return (
                  <React.Fragment key={status.key}>
                    <button
                      onClick={() =>
                        !lead.isConverted && handleLeadStatusClick(status.key)
                      }
                      disabled={lead.isConverted}
                      className={`flex-1 flex flex-col items-center gap-1 sm:gap-2 transition-all min-w-[40px] ${
                        !lead.isConverted
                          ? "cursor-pointer hover:opacity-80"
                          : "cursor-not-allowed opacity-50"
                      }`}
                    >
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                          isActive
                            ? "bg-blue-500 border-blue-500 text-white"
                            : isCompleted
                              ? "bg-green-500 border-green-500 text-white"
                              : isDarkMode
                                ? "bg-gray-700 border-gray-600 text-gray-400"
                                : "bg-gray-200 border-gray-300 text-gray-500"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle size={16} />
                        ) : (
                          <span className="text-xs font-semibold">
                            {index + 1}
                          </span>
                        )}
                      </div>
                      <div className="text-center hidden sm:block">
                        <div
                          className={`text-xs font-medium ${isCurrent ? (isDarkMode ? "text-white" : "text-gray-900") : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {status.label}
                        </div>
                      </div>
                    </button>
                    {index < leadStatuses.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-all ${index < currentIndex ? "bg-green-500" : isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {!lead.isConverted && (
              <div
                className={`mt-3 text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Click on a status to update the lead's progress
              </div>
            )}
          </div>

          {/* Converted Notice */}
          {lead.isConverted && (
            <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-purple-400 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-purple-400 mb-2">
                    This lead has been converted
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm">
                    {relatedRecords.account && (
                      <button
                        onClick={() =>
                          navigate(`/accounts/${relatedRecords.account.id}`)
                        }
                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors"
                      >
                        <Building2 size={14} />
                        <span>View Account: {relatedRecords.account.name}</span>
                        <ExternalLink size={12} />
                      </button>
                    )}
                    {relatedRecords.contact && (
                      <button
                        onClick={() =>
                          navigate(`/contacts/${relatedRecords.contact.id}`)
                        }
                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors"
                      >
                        <User size={14} />
                        <span>
                          View Contact: {relatedRecords.contact.firstName}{" "}
                          {relatedRecords.contact.lastName}
                        </span>
                        <ExternalLink size={12} />
                      </button>
                    )}
                    {lead.convertedOpportunityId &&
                      relatedRecords.opportunities.length > 0 && (
                        <button
                          onClick={() =>
                            navigate(
                              `/opportunities/${lead.convertedOpportunityId}`,
                            )
                          }
                          className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 rounded-lg transition-colors"
                        >
                          <DollarSign size={14} />
                          <span>
                            View Opportunity:{" "}
                            {relatedRecords.opportunities[0].name}
                          </span>
                          <ExternalLink size={12} />
                        </button>
                      )}
                  </div>
                  {lead.convertedAt && (
                    <div
                      className={`text-xs mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Converted on {new Date(lead.convertedAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b overflow-x-auto ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <button
                  onClick={() => setActiveTab("details")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "details"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText size={16} />
                    Details
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("related")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "related"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users size={16} />
                    Related (
                    {relatedRecords.appointments.length +
                      relatedRecords.tasks.length +
                      relatedRecords.opportunities.length}
                    )
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("calls")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "calls"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Phone size={16} />
                    Call History
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("emails")}
                  className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "emails"
                      ? "text-blue-400 border-b-2 border-blue-400"
                      : isDarkMode
                        ? "text-gray-400 hover:text-white"
                        : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Mail size={16} />
                    Emails
                  </div>
                </button>
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab - MATCHING SALESFORCE STRUCTURE */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Lead Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <User size={18} />
                        Lead Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Lead Owner"
                          field="assignedTo"
                          value={lead.assignedTo} // Changed from lead.assignedUser?.name to lead.assignedTo
                          options={users.map((user) => ({
                            value: user.id,
                            label: user.name,
                          }))}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Name"
                          field="fullName"
                          value={lead.fullName}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Company"
                          field="companyName"
                          value={lead.companyName}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Title"
                          field="jobTitle"
                          value={lead.jobTitle}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Lead Source"
                          field="leadSource"
                          value={lead.leadSource}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Lead Status"
                          field="leadStatus"
                          value={lead.leadStatus}
                          options={leadStatuses.map((s) => ({
                            value: s.key,
                            label: s.label,
                          }))}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Industry"
                          field="companyIndustry"
                          value={lead.companyIndustry}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Annual Revenue"
                          field="annualRevenue"
                          value={lead.annualRevenue}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Rating"
                          field="rating"
                          value={lead.rating}
                          options={[
                            { value: "Hot", label: "Hot" },
                            { value: "Warm", label: "Warm" },
                            { value: "Cold", label: "Cold" },
                          ]}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Phone"
                          field="phone"
                          value={lead.phone}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Mobile"
                          field="mobilePhone"
                          value={lead.mobilePhone}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Fax"
                          field="fax"
                          value={lead.fax}
                          type="tel"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Email"
                          field="email"
                          value={lead.email}
                          type="email"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Website"
                          field="companyWebsite"
                          value={lead.companyWebsite}
                          type="url"
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="No. of Handsets"
                          field="numberOfHandsets"
                          value={lead.numberOfHandsets}
                          type="number"
                          onSave={handleFieldSave}
                        />
                        <div className="col-span-1 sm:col-span-2">
                          <EditableField
                            label="Lead Description"
                            field="description"
                            value={lead.description}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                          <EditableField
                            label="Caller Notes"
                            field="callerNotes"
                            value={lead.callerNotes}
                            type="textarea"
                            onSave={handleFieldSave}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <MapPin size={18} />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Street"
                          field="street"
                          value={lead.street}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="City"
                          field="city"
                          value={lead.city}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="State/Province"
                          field="state"
                          value={lead.state}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Zip/Postal Code"
                          field="postalCode"
                          value={lead.postalCode}
                          onSave={handleFieldSave}
                        />
                        <EditableField
                          label="Country"
                          field="country"
                          value={lead.country}
                          onSave={handleFieldSave}
                        />
                      </div>
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {lead.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(lead.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified By
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {lead.lastModifier?.name ||
                              lead.creator?.name ||
                              "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(lead.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related Tab */}
                {activeTab === "related" && (
                  <div className="space-y-6">
                    {/* Appointments */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3
                          className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Appointments ({relatedRecords.appointments.length})
                        </h3>
                        <button
                          onClick={() => setShowBookAppointment(true)}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                        >
                          <Plus size={16} />
                          New
                        </button>
                      </div>
                      {relatedRecords.appointments.length === 0 ? (
                        <p
                          className={
                            isDarkMode
                              ? "text-gray-400 text-sm"
                              : "text-gray-500 text-sm"
                          }
                        >
                          No appointments
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {relatedRecords.appointments.map((apt) => (
                            <div
                              key={apt.id}
                              className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div
                                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {apt.title}
                                  </div>
                                  <div
                                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {new Date(apt.startTime).toLocaleString()}
                                  </div>
                                </div>
                                <span
                                  className={`text-xs px-2 py-1 rounded ${
                                    apt.status === "completed"
                                      ? "bg-green-500/20 text-green-400"
                                      : apt.status === "cancelled"
                                        ? "bg-red-500/20 text-red-400"
                                        : "bg-blue-500/20 text-blue-400"
                                  }`}
                                >
                                  {apt.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Tasks */}
                    <div
                      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3
                          className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Tasks ({relatedRecords.tasks.length})
                        </h3>
                        <button
                          onClick={() => setShowCreateTask(true)}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
                        >
                          <Plus size={16} />
                          New
                        </button>
                      </div>
                      {relatedRecords.tasks.length === 0 ? (
                        <p
                          className={
                            isDarkMode
                              ? "text-gray-400 text-sm"
                              : "text-gray-500 text-sm"
                          }
                        >
                          No tasks
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {relatedRecords.tasks.map((task) => (
                            <div
                              key={task.id}
                              className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div
                                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {task.title}
                                  </div>
                                  <div
                                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    Due:{" "}
                                    {new Date(
                                      task.dueDate,
                                    ).toLocaleDateString()}
                                  </div>
                                </div>
                                <span
                                  className={`text-xs px-2 py-1 rounded ${
                                    task.status === "completed"
                                      ? "bg-green-500/20 text-green-400"
                                      : task.priority === "high"
                                        ? "bg-red-500/20 text-red-400"
                                        : "bg-blue-500/20 text-blue-400"
                                  }`}
                                >
                                  {task.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Opportunities */}
                    {relatedRecords.opportunities.length > 0 && (
                      <div
                        className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                      >
                        <h3
                          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          Opportunities ({relatedRecords.opportunities.length})
                        </h3>
                        <div className="space-y-2">
                          {relatedRecords.opportunities.map((opp) => (
                            <div
                              key={opp.id}
                              className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
                              onClick={() =>
                                navigate(`/opportunities/${opp.id}`)
                              }
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div
                                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {opp.name}
                                  </div>
                                  <div
                                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    £{opp.amount?.toLocaleString()} •{" "}
                                    {opp.stage}
                                  </div>
                                </div>
                                <span
                                  className={`text-xs px-2 py-1 rounded ${
                                    opp.status === "won"
                                      ? "bg-green-500/20 text-green-400"
                                      : opp.status === "lost"
                                        ? "bg-red-500/20 text-red-400"
                                        : "bg-blue-500/20 text-blue-400"
                                  }`}
                                >
                                  {opp.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === "calls" && (
                  <div
                    className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <h2
                      className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Call History
                    </h2>
                    <CallNotesTimeline
                      entityType="lead" // or "contact", "case", "account"
                      entityId={id}
                    />
                  </div>
                )}
                {activeTab === "emails" && (
                  <div
                    className={`border rounded-lg p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2
                        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Emails
                      </h2>
                      <button
                        onClick={() => setShowSendEmail(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Send Email
                      </button>
                    </div>
                    <LeadEmailThread leadId={id} leadData={lead} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Notes */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex items-center justify-between mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <h3 className="font-semibold">Notes ({notes.length})</h3>
              </div>

              {/* Add Note */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg mb-2 focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              {/* Notes List */}
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>

            {/* Sales Engagement Step History */}
            {engagementSteps && engagementSteps.length > 0 && (
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <ListOrdered
                    size={16}
                    className={isDarkMode ? "text-blue-400" : "text-blue-600"}
                  />
                  <h3
                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Engagement History
                  </h3>
                  <span
                    className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"}`}
                  >
                    {engagementSteps.length}
                  </span>
                </div>
                <div className="relative">
                  {/* Vertical timeline line */}
                  <div
                    className={`absolute left-[7px] top-2 bottom-2 w-px ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  />
                  <div className="space-y-4">
                    {engagementSteps.map((step, index) => {
                      const stepLabels = {
                        not_contacted: "Not Contacted",
                        email_sent: "Email Sent",
                        call_1: "Call 1",
                        call_2: "Call 2",
                        call_3: "Call 3",
                        call_4: "Call 4",
                        qualified: "Qualified",
                        unqualified: "Unqualified",
                        call_back_later: "Call Back Later",
                      };
                      const outcomeLabels = {
                        connected: "Connected",
                        no_answer: "No Answer",
                        voicemail: "Voicemail",
                        wrong_number: "Wrong Number",
                        busy: "Busy",
                        disconnected: "Disconnected",
                      };
                      const isLast = index === engagementSteps.length - 1;
                      const stepColors = {
                        qualified: "bg-green-500",
                        unqualified: "bg-red-500",
                        email_sent: "bg-blue-500",
                        call_back_later: "bg-yellow-500",
                      };
                      const dotColor =
                        stepColors[step.step] ||
                        (isDarkMode ? "bg-gray-500" : "bg-gray-400");

                      return (
                        <div key={step.id} className="flex gap-3 relative">
                          {/* Timeline dot */}
                          <div
                            className={`w-3.5 h-3.5 rounded-full mt-1 flex-shrink-0 z-10 ring-2 ${dotColor} ${isDarkMode ? "ring-[#13161B]" : "ring-white"}`}
                          />
                          <div className="flex-1 min-w-0 pb-1">
                            <div className="flex items-start justify-between gap-2">
                              <span
                                className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                              >
                                {stepLabels[step.step] || step.step}
                              </span>
                              {step.completedAt && (
                                <span
                                  className={`text-xs flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  {new Date(
                                    step.completedAt,
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {step.callOutcome && (
                              <span
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {outcomeLabels[step.callOutcome] ||
                                  step.callOutcome}
                                {step.callDuration
                                  ? ` · ${Math.floor(step.callDuration / 60)}m ${step.callDuration % 60}s`
                                  : ""}
                              </span>
                            )}
                            {step.notes && (
                              <p
                                className={`text-xs mt-1 truncate ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                title={step.notes}
                              >
                                {step.notes}
                              </p>
                            )}
                            {step.callbackDate && (
                              <p
                                className={`text-xs mt-0.5 ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}
                              >
                                Callback:{" "}
                                {new Date(
                                  step.callbackDate,
                                ).toLocaleDateString()}
                                {step.callbackTime
                                  ? ` at ${step.callbackTime}`
                                  : ""}
                              </p>
                            )}
                            {step.completedByUser?.name && (
                              <p
                                className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                              >
                                by {step.completedByUser.name}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Company Info */}
            {lead.companyName && (
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Company Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Company Name
                    </label>
                    <p
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {lead.companyName}
                    </p>
                  </div>
                  {lead.companyWebsite && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Website
                      </label>
                      <a
                        href={lead.companyWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        {lead.companyWebsite}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  )}
                  {lead.companyIndustry && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Industry
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {lead.companyIndustry}
                      </p>
                    </div>
                  )}
                  {lead.numberOfHandsets && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Handsets
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {lead.numberOfHandsets.toLocaleString()}
                      </p>
                    </div>
                  )}
                  {lead.customFields?.originalWebhookData?.maxExpectedUsers && (
                    <div>
                      <label
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Max Expected Users
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {Number(
                          lead.customFields.originalWebhookData
                            .maxExpectedUsers,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditLeadModal
          lead={lead}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchLead();
          }}
        />
      )}

      {showBookAppointment && (
        <BookAppointmentModal
          lead={lead}
          onClose={() => setShowBookAppointment(false)}
          onSuccess={() => {
            setShowBookAppointment(false);
            fetchRelatedRecords();
          }}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal
          lead={lead}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => {
            setShowCreateTask(false);
            fetchRelatedRecords();
          }}
        />
      )}

      {showConvertModal && (
        <ConvertLeadModal
          lead={lead}
          onClose={() => setShowConvertModal(false)}
          onSuccess={() => {
            setShowConvertModal(false);
            fetchLead();
            fetchRelatedRecords();
          }}
        />
      )}

      {showSendEmail && (
        <SendLeadEmailModal
          leadData={lead}
          onClose={() => setShowSendEmail(false)}
          onSuccess={() => {
            setShowSendEmail(false);
            fetchLead();
          }}
        />
      )}

      {showLogCall && (
        <LogCallModal
          lead={lead}
          onClose={() => setShowLogCall(false)}
          onSuccess={() => {
            setShowLogCall(false);
            fetchLead();
          }}
        />
      )}
    </div>
  );
};

export default LeadDetail;
